import { ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  useBodyMeasurementEntry,
  useLatestBodyMeasurementBefore,
} from "@/hooks/useBodyMeasurements";
import {
  catalogTypesFor,
  formatEntryDate,
  formatMeasurementValue,
  localDateOf,
  parseMeasurementInput,
} from "@/lib/body-measurements";
import { addDays, DAY_KEYS } from "@/lib/days";
import { setBodyMeasurementValue } from "@/lib/repos/body-measurements";
import type { BodyMeasurementLogConfig } from "@/lib/schema";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";

type Props = WidgetDayViewProps<BodyMeasurementLogConfig>;

export function DayView(props: Props) {
  // Entries are date-keyed and global — the viewed day maps to a date via
  // the week's startDate; the settings preview falls back to today.
  const date = props.week
    ? addDays(props.week.startDate, DAY_KEYS.indexOf(props.dayKey))
    : localDateOf(new Date());

  // Keyed by date so drafts and the manual collapse override never leak
  // into another day when navigating within a mounted week.
  return <DateView key={date} date={date} {...props} />;
}

function DateView({
  config,
  date,
  dayKey,
  readOnly = false,
}: Props & { date: string }) {
  const { entry, loading } = useBodyMeasurementEntry(date);
  const { latestByType } = useLatestBodyMeasurementBefore(date);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const types = catalogTypesFor(config.types);
  // Ferdiglogget (CONTEXT.md): instance-relative — only the types this
  // instance displays count, whatever else the global entry holds. A date
  // you simply do not weigh yourself stays outstanding; there is no skip
  // marker, because the values live globally per date while any such flag
  // would live inside the week (docs/adr/0002).
  const loggingCompleted =
    !readOnly &&
    types.length > 0 &&
    types.every((type) => typeof entry?.values[type.key] === "number");

  // `ready` is !loading: the entry starts null, which reads as not logged and
  // would otherwise pin an already-logged day open.
  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted, !loading);
  const collapsible = !readOnly && types.length > 0;
  const collapsed = collapsible && !open;

  const commit = async (typeKey: (typeof config.types)[number]) => {
    const draft = drafts[typeKey];
    setDrafts((d) => {
      const next = { ...d };
      delete next[typeKey];
      return next;
    });
    if (draft === undefined) return;
    const stored = entry?.values[typeKey];
    if (draft.trim().length === 0) {
      if (typeof stored === "number") {
        await setBodyMeasurementValue(date, typeKey, null);
      }
      return;
    }
    const parsed = parseMeasurementInput(draft);
    // Invalid input reverts to the stored value (draft already dropped).
    if (parsed === null || parsed === stored) return;
    await setBodyMeasurementValue(date, typeKey, parsed);
  };

  return (
    <section
      className={`widget-card body-measurement-log${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        {collapsible ? (
          <button
            type="button"
            className="widget-card-toggle"
            onClick={toggle}
            aria-label={collapsed ? "Utvid" : "Skjul"}
            aria-expanded={!collapsed}
            data-testid={`widget-collapse-${config.id}`}
          >
            <ChevronDown className="chev" size={16} />
          </button>
        ) : null}
      </header>
      {collapsed ? (
        <p
          className="measurement-summary"
          data-testid={`body-measurement-summary-${config.id}`}
        >
          {types
            .map((type) => {
              const stored = entry?.values[type.key];
              const value =
                typeof stored === "number"
                  ? `${formatMeasurementValue(stored)} ${type.unit}`
                  : "–";
              return `${type.label}: ${value}`;
            })
            .join(" · ")}
        </p>
      ) : null}
      <div className="widget-card-body">
        {types.map((type) => {
          const stored = entry?.values[type.key];
          const value =
            drafts[type.key] ??
            (typeof stored === "number" ? formatMeasurementValue(stored) : "");
          const last = latestByType[type.key];
          const showHint = value.length === 0 && last !== undefined;
          const inputId = `measurement-${config.id}-${type.key}`;
          return (
            <div className="measurement-row" key={type.key}>
              <label className="measurement-label" htmlFor={inputId}>
                {type.label}
              </label>
              <div className="measurement-control">
                <input
                  id={inputId}
                  className="input measurement-input"
                  type="text"
                  inputMode="decimal"
                  disabled={readOnly}
                  value={value}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [type.key]: e.target.value }))
                  }
                  onBlur={() => void commit(type.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  data-testid={`body-measurement-input-${config.id}-${type.key}`}
                />
                <span className="measurement-unit">{type.unit}</span>
              </div>
              {showHint ? (
                <p
                  className="hint measurement-hint"
                  data-testid={`body-measurement-last-hint-${config.id}-${type.key}`}
                >
                  Sist: {formatMeasurementValue(last.value)} {type.unit} ·{" "}
                  {formatEntryDate(last.date)}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
